import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Crown, Shield, Star, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const tiers = [
  {
    id: 'platinum',
    name: 'Platinum',
    icon: Crown,
    color: 'from-amber-400 to-yellow-500',
    border: 'border-amber-400/50',
    bg: 'from-amber-900/40 to-yellow-900/40',
    text: 'text-amber-300',
    benefits: [
      'Unlimited WhatsApp support with therapist',
      'Priority booking for all sessions',
      'Monthly wellness check-in call',
      'Exclusive quantum healing tips & content',
      '20% discount on all future sessions',
      'Free annual Prana re-analysis'
    ]
  },
  {
    id: 'gold',
    name: 'Gold',
    icon: Star,
    color: 'from-yellow-500 to-orange-500',
    border: 'border-yellow-400/40',
    bg: 'from-yellow-900/30 to-orange-900/30',
    text: 'text-yellow-300',
    benefits: [
      'WhatsApp support (Mon-Sat)',
      'Priority booking for sessions',
      'Quarterly wellness check-in',
      'Quantum healing content access',
      '10% discount on future sessions'
    ]
  },
  {
    id: 'silver',
    name: 'Silver',
    icon: Shield,
    color: 'from-gray-300 to-gray-400',
    border: 'border-gray-400/40',
    bg: 'from-gray-800/30 to-slate-800/30',
    text: 'text-gray-300',
    benefits: [
      'WhatsApp support (weekdays)',
      'Standard booking access',
      'Community member benefits',
      '5% discount on future sessions'
    ]
  }
];

const MembershipSection = () => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', whatsapp: '', previous_sessions: 1, health_concern: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Please fill in name, email and phone');
      return;
    }
    if (!selectedTier) {
      toast.error('Please select a membership tier');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/memberships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, tier: selectedTier })
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Membership application submitted!');
      } else {
        throw new Error('Failed');
      }
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="bg-amber-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-400/40">
          <Crown className="w-10 h-10 text-amber-400" />
        </div>
        <h4 className="text-2xl font-bold text-white mb-3">Welcome to the Divine Quanta Family!</h4>
        <p className="text-purple-200 mb-4">Your {tiers.find(t => t.id === selectedTier)?.name} membership application has been received.</p>
        <p className="text-amber-300 font-semibold mb-6">Please complete payment of Rs 500 via WhatsApp to activate your membership.</p>
        <a
          href="https://wa.me/918920443069?text=Hi,%20I%20have%20applied%20for%20Divine%20Quanta%20membership.%20Please%20share%20payment%20details%20for%20Rs%20500%20membership%20fee."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all"
          data-testid="membership-pay-whatsapp"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
          Pay Rs 500 via WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Tier Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const Icon = tier.icon;
          const isSelected = selectedTier === tier.id;
          return (
            <button
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              className={`relative bg-gradient-to-br ${tier.bg} backdrop-blur-sm border-2 ${isSelected ? tier.border + ' scale-105' : 'border-white/10'} rounded-2xl p-6 text-left transition-all hover:scale-[1.02] cursor-pointer`}
              data-testid={`membership-tier-${tier.id}`}
            >
              {isSelected && (
                <div className="absolute -top-3 -right-3 bg-green-500 rounded-full p-1">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
              <div className={`bg-gradient-to-r ${tier.color} w-14 h-14 rounded-full flex items-center justify-center mb-4`}>
                <Icon className="w-7 h-7 text-black" />
              </div>
              <h4 className={`text-xl font-bold ${tier.text} mb-1`}>{tier.name}</h4>
              <p className="text-white/60 text-sm mb-4">Membership Tier</p>
              <ul className="space-y-2">
                {tier.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-purple-200">
                    <Check className={`w-4 h-4 ${tier.text} flex-shrink-0 mt-0.5`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Price highlight */}
      <div className="text-center">
        <div className="inline-block bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-400/40 rounded-2xl px-10 py-6 backdrop-blur-sm">
          <p className="text-amber-200 text-sm mb-1">One-Time Membership Fee</p>
          <p className="text-5xl font-bold text-white mb-1">Rs 500</p>
          <p className="text-amber-300 text-sm">For all tiers - Lifetime membership</p>
        </div>
      </div>

      {/* Registration Form */}
      <div className="bg-purple-900/30 border border-purple-400/20 rounded-2xl p-8 max-w-2xl mx-auto">
        <h4 className="text-xl font-bold text-white mb-6 text-center">Become a Member</h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <input
            type="text" placeholder="Full Name *" value={formData.name}
            onChange={(e) => setFormData(p => ({...p, name: e.target.value}))}
            className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
            data-testid="membership-name"
          />
          <input
            type="email" placeholder="Email *" value={formData.email}
            onChange={(e) => setFormData(p => ({...p, email: e.target.value}))}
            className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
            data-testid="membership-email"
          />
          <input
            type="tel" placeholder="Phone *" value={formData.phone}
            onChange={(e) => setFormData(p => ({...p, phone: e.target.value}))}
            className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
            data-testid="membership-phone"
          />
          <input
            type="number" placeholder="Past Sessions (approx)" value={formData.previous_sessions}
            onChange={(e) => setFormData(p => ({...p, previous_sessions: parseInt(e.target.value) || 0}))}
            className="bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none"
            data-testid="membership-sessions"
          />
        </div>
        <textarea
          placeholder="Current health concern or reason for continued membership (optional)"
          value={formData.health_concern}
          onChange={(e) => setFormData(p => ({...p, health_concern: e.target.value}))}
          className="w-full bg-purple-900/40 border border-purple-400/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:border-purple-400 focus:outline-none mb-4 h-24 resize-none"
          data-testid="membership-concern"
        />
        <div className="text-center">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedTier}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black px-10 py-4 rounded-full font-bold text-lg"
            data-testid="membership-submit-btn"
          >
            {submitting ? 'Submitting...' : `Apply for ${selectedTier ? tiers.find(t => t.id === selectedTier)?.name : ''} Membership - Rs 500`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MembershipSection;
